import { notify } from '@tiger/bugsnag';
import { clearCache, loadCG } from '@tiger/cg';
import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import 'utils/http';
import { mapValues } from 'lodash';
import APPStore from 'stores/APP';
import { IS_UAT, urlQuery } from '../constants';
// 导入接口定义
import * as APIS from './apis/API';
import * as CG from './apis/CG';
import * as CUSTOMER from './apis/CUSTOMER';
import * as DEPOSIT from './apis/DEPOSIT';
import * as HQ from './apis/HQ';
import * as INFOBASE from './apis/INFOBASE';
import * as MS from './apis/MS';
import * as OAUTH from './apis/OAUTH';
import * as OAUTH_CENTER from './apis/OAUTH_CENTER';
import * as OAUTH_USER from './apis/OAUTH_USER';
import * as PC from './apis/PC';
import * as PORTAL from './apis/PORTAL';
import * as SUPPORT from './apis/SUPPORT';
import * as TESSERACT from './apis/TESSERACT';
import * as TRADE from './apis/TRADE';

export const APICollection = {
    OAUTH,
    OAUTH_CENTER,
    OAUTH_USER,
    CUSTOMER,
    CG,
    API: APIS,
    TESSERACT,
    DEPOSIT,
    SUPPORT,
    PC,
    MS,
    INFOBASE,
    TRADE,
    PORTAL,
    HQ
};

interface APIInstance {
    (...args: Array<string | number>): string;

    request<T = any>(config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
    get<T = any>(...args: Array<string | number>): AxiosPromise<T>;
    get<T = any>(config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
    delete(...args: Array<string | number>): AxiosPromise;
    delete(config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise;
    head(...args: Array<string | number>): AxiosPromise;
    head(config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise;

    post<T = any>(...args: Array<string | number>): AxiosPromise<T>;
    post<T = any>(configOrData: AxiosRequestConfig | any, ...args: Array<string | number>): AxiosPromise<T>;
    post<T = any>(data: any, config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;

    put<T = any>(...args: Array<string | number>): AxiosPromise<T>;
    put<T = any>(configOrData: AxiosRequestConfig | any, ...args: Array<string | number>): AxiosPromise<T>;
    put<T = any>(data: any, config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;

    patch<T = any>(...args: Array<string | number>): AxiosPromise<T>;
    patch<T = any>(configOrData: AxiosRequestConfig | any, ...args: Array<string | number>): AxiosPromise<T>;
    patch<T = any>(data: any, config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
}

type APIPicker<T extends Record<string, any>> = { [P in keyof T]: API<T[P]['API']> };
type API<T> = { [P in keyof T]: T[P] extends object ? API<T[P]> : APIInstance };

const isDev = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'development';

export const axiosMethods = ['request', 'get', 'post', 'put', 'delete', 'head', 'patch'];

export default Object.keys(APICollection).reduce((exports, host) => {
    exports[host] = enhanced(APICollection[host]);

    return exports;
}, {}) as APIPicker<typeof APICollection>;

function enhanced(config) {
    const createAPI = apis =>
        Object.keys(apis).reduce((result, key) => {
            const pathname = apis[key];

            function apiInstance(...args) {
                let index = 0;
                const host = isDev ? config.HOST[0] : config.HOST[1];

                return (
                    host +
                    pathname.replace(/:[^/]+/gi, match => {
                        const arg = args[index++];

                        return arg === undefined ? match : arg;
                    })
                );
            }

            axiosMethods.forEach(type => {
                apiInstance[type] = (...args) => {
                    const apiArgs: Array<string | number> = [];
                    const httpArgs: object[] = [];

                    args.forEach(item => {
                        if (typeof item === 'string' || typeof item === 'number') {
                            apiArgs.push(item);
                        } else {
                            httpArgs.push(item);
                        }
                    });

                    // 针对request方法特殊处理
                    if (type === 'request') {
                        return axios.request({
                            ...httpArgs[0],
                            url: apiInstance(...apiArgs)
                        });
                    }

                    return axios[type](apiInstance(...apiArgs), ...httpArgs);
                };
            });

            result[key] = typeof pathname === 'string' ? apiInstance : createAPI(pathname);

            return result;
        }, {});

    return createAPI(config.API);
}

/**
 * 根据配置的HOST_ALIAS切换host
 */
export function setAlias(name: string) {
    Object.keys(APICollection).forEach(host => {
        const config = APICollection[host];

        if (config.HOST_ALIAS && name in config.HOST_ALIAS) {
            config.HOST.splice(0, 2, ...config.HOST_ALIAS[name]);
        }
    });
}

/**
 * 接口环境切换
 * 注：切换时，需要清理本地cg缓存
 */
const API_ENV_DATA_CACHE_KEY = 'QAX';

export function saveAPIEnv(env: string) {
    localStorage.setItem(API_ENV_DATA_CACHE_KEY, env);
    clearCache();
}

/**
 * 清理/重置接口环境
 * 注：重置时，需要清理本地cg缓存
 */
export function removeAPIEnv() {
    localStorage.removeItem(API_ENV_DATA_CACHE_KEY);
    clearCache();
}

// UAT环境和测试环境 支持从query中读取_QAX，本地localstorage的QAX，以及UAT环境下默认使用prod-tenant配置
export function getAPIEnv(): string | undefined {
    if (!IS_UAT && !__DEV__) {
        return undefined;
    }

    const qax = urlQuery._QAX;

    return (
        (Array.isArray(qax) ? qax[0] : qax) ||
        localStorage.getItem(API_ENV_DATA_CACHE_KEY) ||
        (IS_UAT ? 'prod-tenant' : undefined)
    );
}

export async function preloadCG() {
    try {
        // 测试环境读取浏览器的env，并实时拉取对应的cg配置
        const environment = getAPIEnv();

        // 指定的测试环境，APP内环境 均需获取cg信息
        // tigertrade内接入cg，线上美国侧先不接入cg环境
        // uat环境
        if (
            APPStore.isTigerTrade ||
            (process.env.NODE_ENV !== 'production' && (environment || APPStore.isTiger)) ||
            IS_UAT
        ) {
            const cgData = await loadCG({
                tenantId: APPStore.tenantId,
                fallback: true,
                environment,
                appName: APPStore.isTigerTrade ? 'TigerTrade' : 'TradeUP'
            });

            if (cgData) {
                Object.assign(
                    CG_API,
                    APPStore.isTigerTrade
                        ? cgData
                        : mapValues(cgData, config => {
                              // tradeup配置，如果没有配置Marsco，则将COMMON作为Marsco添加进来。这是因为我们在API文件中，是以明确存在Marsco配置的方式来调用的
                              // @ts-ignore
                              if (config && config.COMMON && !config.TBUS) {
                                  // @ts-ignore
                                  config.TBUS = config.COMMON;
                              }

                              return config;
                          })
                );

                Object.keys(APICollection).forEach(host => {
                    APICollection[host].bindCG?.();
                });
            }
        }
    } catch (err) {
        notify(err);
    }
}
